import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import apiService from '../../../services/api';
import './Layout.css';
import logoImage from '../../../assets/icon_logo.png';

function Layout({ children }) {
  const auth = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchDropdown, setShowSearchDropdown] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [isNavbarVisible, setIsNavbarVisible] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const searchDebounceRef = useRef(null);
  const searchDropdownRef = useRef(null);
  const lastScrollY = useRef(0);
  const navbarRef = useRef(null);

  // Fetch current user data from database (not Cognito)
  useEffect(() => {
    const fetchCurrentUser = async () => {
      if (!auth.isAuthenticated || !auth.user?.id_token) {
        return;
      }

      try {
        const idToken = auth.user.id_token;
        const userData = await apiService.getCurrentUser(idToken);
        setCurrentUser(userData);

        // Check if user has accepted terms and conditions
        if (!userData.terms_accepted) {
          navigate('/terms');
        }
      } catch (err) {
        console.error('Error fetching current user:', err);
      }
    };

    fetchCurrentUser();
  }, [auth.isAuthenticated, auth.user, refreshTrigger, navigate]);

  // Listen for profile update events
  useEffect(() => {
    const handleProfileUpdate = () => {
      setRefreshTrigger(prev => prev + 1);
    };

    window.addEventListener('profileUpdated', handleProfileUpdate);
    return () => window.removeEventListener('profileUpdated', handleProfileUpdate);
  }, []);

  // Use database data for display, fallback to 'User' if not loaded yet
  const userData = {
    displayName: currentUser?.display_name || 'User',
    initials: currentUser?.display_name
      ? currentUser.display_name.split(' ').map(n => n[0]).join('').toUpperCase().substring(0, 2)
      : 'U'
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchDropdownRef.current && !searchDropdownRef.current.contains(event.target)) {
        setShowSearchDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Update navbar height CSS variable
  useEffect(() => {
    const updateNavbarHeight = () => {
      if (navbarRef.current) {
        const height = navbarRef.current.offsetHeight;
        document.documentElement.style.setProperty('--navbar-height', `${height}px`);
      }
    };

    updateNavbarHeight();
    window.addEventListener('resize', updateNavbarHeight);
    return () => window.removeEventListener('resize', updateNavbarHeight);
  }, []);

  // Handle navbar hide/show on scroll
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;

      // Show navbar when scrolling up, hide when scrolling down
      // Always show navbar when at the top of the page
      if (currentScrollY < 10) {
        setIsNavbarVisible(true);
      } else if (currentScrollY < lastScrollY.current) {
        // Scrolling up
        setIsNavbarVisible(true);
      } else if (currentScrollY > lastScrollY.current && currentScrollY > 100) {
        // Scrolling down and past 100px
        setIsNavbarVisible(false);
      }

      lastScrollY.current = currentScrollY;
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Debounced search effect
  useEffect(() => {
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }

    if (searchQuery.trim().length < 2) {
      setSearchResults([]);
      setShowSearchDropdown(false);
      return;
    }

    setIsSearching(true);

    searchDebounceRef.current = setTimeout(async () => {
      try {
        if (auth.user?.id_token) {
          const data = await apiService.searchUsers(auth.user.id_token, searchQuery.trim(), 10);
          setSearchResults(data.results || []);
          setShowSearchDropdown(true);
        }
      } catch (err) {
        console.error('Search error:', err);
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    }, 500);

    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
    };
  }, [searchQuery, auth.user]);

  const handleSignOut = () => {
    auth.signOut();
    // No need to manually redirect - App.js will handle showing login page
  };

  const handleSearch = (e) => {
    e.preventDefault();
    // Search is handled by the debounced effect
  };

  const handleSelectUser = (user) => {
    setSearchQuery('');
    setShowSearchDropdown(false);
    setSearchResults([]);

    // Navigate to organization page for organizations, profile page for individuals and developers
    if (user.user_type === 'organization') {
      // Prefer slug over UUID for cleaner URLs
      const orgIdentifier = user.slug || user.user_id;
      navigate(`/organization/${orgIdentifier}`);
    } else {
      navigate(`/profile/${user.user_id}`);
    }
  };

  const handleNavigate = (path) => {
    navigate(path);
    setShowMobileMenu(false);
  };

  return (
    <div className="app-layout">
      {/* App Bar */}
      <header ref={navbarRef} className={`app-bar ${isNavbarVisible ? 'visible' : 'hidden'}`}>
        <div className="app-bar-left">
          <div className="logo" onClick={() => navigate('/')}>
            <img className="logo-icon" src={logoImage} alt="Logo" />
            <span className="logo-text">CoinedIn</span>
          </div>
          <nav className="nav-menu">
            <button
              className={`nav-item ${location.pathname === '/' ? 'active' : ''}`}
              onClick={() => navigate('/')}
            >
              Home
            </button>
            {currentUser?.user_type !== 'organization' && auth.user?.profile?.sub && (
              <button
                className={`nav-item ${location.pathname === `/profile/${auth.user.profile.sub}/coins` ? 'active' : ''}`}
                onClick={() => navigate(`/profile/${auth.user.profile.sub}/coins`)}
              >
                Coins
              </button>
            )}
            {/* Organization navigation - temporarily disabled */}
            {/* {currentUser?.user_type === 'organization' ? (
              <button
                className={`nav-item ${location.pathname.startsWith('/organization/') ? 'active' : ''}`}
                onClick={() => {
                  const orgIdentifier = currentUser.slug || currentUser.user_id;
                  navigate(`/organization/${orgIdentifier}`);
                }}
              >
                Organization
              </button>
            ) : (
              <button
                className={`nav-item ${location.pathname === '/my-organizations' ? 'active' : ''}`}
                onClick={() => navigate('/my-organizations')}
              >
                My Organizations
              </button>
            )} */}
            {auth.user?.profile?.sub && (
              <button
                className={`nav-item ${location.pathname === `/profile/${auth.user.profile.sub}` ? 'active' : ''}`}
                onClick={() => navigate(`/profile/${auth.user.profile.sub}`)}
              >
                Profile
              </button>
            )}
          </nav>
          <button
            className="hamburger-menu"
            onClick={() => setShowMobileMenu(!showMobileMenu)}
            aria-label="Toggle menu"
          >
            <span className={`hamburger-line ${showMobileMenu ? 'open' : ''}`}></span>
            <span className={`hamburger-line ${showMobileMenu ? 'open' : ''}`}></span>
            <span className={`hamburger-line ${showMobileMenu ? 'open' : ''}`}></span>
          </button>
        </div>

        <div className="app-bar-center" ref={searchDropdownRef}>
          <form className="search-bar" onSubmit={handleSearch}>
            <span className="search-icon">&#128269;</span>
            <input
              type="text"
              placeholder="Search organizations and users..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="search-input"
            />
            {isSearching && (
              <div className="search-spinner-header">
                <div className="spinner-small"></div>
              </div>
            )}
          </form>

          {showSearchDropdown && (
            <div className="header-search-dropdown">
              {searchResults.length === 0 ? (
                <div className="no-results-header">
                  No users found matching "{searchQuery}"
                </div>
              ) : (
                <ul className="results-list-header">
                  {searchResults.map((user) => (
                    <li
                      key={user.user_id}
                      className="result-item-header"
                      onClick={() => handleSelectUser(user)}
                    >
                      <div className="user-info-header">
                        <div className="user-name-header">{user.display_name}</div>
                        <div className="user-email-header">{user.email}</div>
                      </div>
                      <div className={`user-type-badge-header ${user.user_type}`}>
                        <span className="badge-icon-header">
                          {user.user_type === 'individual' ? '👤' : user.user_type === 'developer' ? '💻' : '🏢'}
                        </span>
                        <span className="badge-text-header">
                          {user.user_type === 'individual' ? 'Individual' : user.user_type === 'developer' ? 'Developer' : 'Organization'}
                        </span>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>

        <div className="app-bar-right">
          <button
            className="report-bug-button"
            onClick={() => navigate('/report-bug', {
              state: {
                fromPage: window.location.href,
                fromPath: window.location.pathname
              }
            })}
            title="Report a Bug"
          >
            <span className="bug-icon">🐛</span>
            Report Bug
          </button>

          <div className="profile-dropdown">
            <button
              className="profile-button"
              onClick={() => setShowProfileMenu(!showProfileMenu)}
            >
              <div className="profile-avatar">
                {userData.initials}
              </div>
              <span className="profile-name">{userData.displayName}</span>
              <span className="dropdown-arrow">&#9660;</span>
            </button>

            {showProfileMenu && (
              <div className="dropdown-menu">
                <button onClick={() => {
                  const userId = auth.user?.profile?.sub;
                  if (userId) {
                    navigate(`/profile/${userId}`);
                    setShowProfileMenu(false);
                  }
                }} className="dropdown-item">
                  <span className="dropdown-icon">&#128100;</span>
                  My Profile
                </button>
                <button onClick={() => {
                  navigate('/settings');
                  setShowProfileMenu(false);
                }} className="dropdown-item">
                  <span className="dropdown-icon">&#9881;</span>
                  Settings
                </button>
                <button onClick={() => {
                  navigate('/bug-reports');
                  setShowProfileMenu(false);
                }} className="dropdown-item">
                  <span className="dropdown-icon">&#128203;</span>
                  Bug Reports
                </button>
                <div className="dropdown-divider"></div>
                <button onClick={handleSignOut} className="dropdown-item">
                  <span className="dropdown-icon">&#128682;</span>
                  Log Out
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Mobile Navigation Menu */}
      {showMobileMenu && (
        <div className={`mobile-menu ${isNavbarVisible ? 'visible' : 'hidden'}`}>
          <button
            className={`mobile-menu-item ${location.pathname === '/' ? 'active' : ''}`}
            onClick={() => handleNavigate('/')}
          >
            <span className="mobile-menu-icon">🏠</span>
            Home
          </button>
          {currentUser?.user_type !== 'organization' && auth.user?.profile?.sub && (
            <button
              className={`mobile-menu-item ${location.pathname === `/profile/${auth.user.profile.sub}/coins` ? 'active' : ''}`}
              onClick={() => handleNavigate(`/profile/${auth.user.profile.sub}/coins`)}
            >
              <span className="mobile-menu-icon">💰</span>
              Coins
            </button>
          )}
          {/* Organization mobile navigation - temporarily disabled */}
          {/* {currentUser?.user_type === 'organization' ? (
            <button
              className={`mobile-menu-item ${location.pathname.startsWith('/organization/') ? 'active' : ''}`}
              onClick={() => {
                const orgIdentifier = currentUser.slug || currentUser.user_id;
                handleNavigate(`/organization/${orgIdentifier}`);
              }}
            >
              <span className="mobile-menu-icon">🏢</span>
              Organization
            </button>
          ) : (
            <button
              className={`mobile-menu-item ${location.pathname === '/my-organizations' ? 'active' : ''}`}
              onClick={() => handleNavigate('/my-organizations')}
            >
              <span className="mobile-menu-icon">🏢</span>
              My Organizations
            </button>
          )} */}
          {auth.user?.profile?.sub && (
            <button
              className={`mobile-menu-item ${location.pathname === `/profile/${auth.user.profile.sub}` ? 'active' : ''}`}
              onClick={() => handleNavigate(`/profile/${auth.user.profile.sub}`)}
            >
              <span className="mobile-menu-icon">👤</span>
              Profile
            </button>
          )}
        </div>
      )}

      {/* Main Content */}
      <main className="main-layout-content">
        {children}
      </main>
    </div>
  );
}

export default Layout;
